import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, BookOpen, Users, Edit3, RotateCcw, X, Zap, Archive, ArchiveRestore, Eye, ArrowLeft, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Course, StudySession, coursesApi, authApi } from '../services/api';
import { analytics } from '../services/analytics';
import { MixedText } from '../utils/rtl';
import { useOnboarding } from '../hooks/useOnboarding';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, SpaceCard } from './ui/Card';
import { Button } from './ui/Button';
import { DashboardContainer } from './layout/PageContainer';
import UpgradeModal from './credits/UpgradeModal';
import FeedbackChatWidget from './FeedbackChatWidget';

const CourseDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [courses, setCourses] = useState<Course[]>([]);
  const [sessions, setSessions] = useState<{ [courseId: number]: StudySession[] }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateCourse, setShowCreateCourse] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [newCourseDescription, setNewCourseDescription] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCourses, setArchivedCourses] = useState<Course[]>([]);
  
  // Edit course state
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [editCourseName, setEditCourseName] = useState('');
  const [editCourseDescription, setEditCourseDescription] = useState('');
  
  // Easter egg state for special user
  const [showSpecialEasterEgg, setShowSpecialEasterEgg] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [easterEggText, setEasterEggText] = useState<any>(null);
  
  const navigate = useNavigate();

  // Onboarding hook for dev reset functionality
  const { resetOnboarding, shouldShowOnboarding } = useOnboarding();

  // Dev modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Feedback chat state
  const [showFeedbackChat, setShowFeedbackChat] = useState(false);

  // Check if we're in development mode
  const isDev = process.env.NODE_ENV === 'development';

  const maybeRedirectToOnboarding = (courseList: Course[]) => {
    const userHasCourses = courseList.length > 0;
    if (shouldShowOnboarding(userHasCourses)) {
      analytics.track('Onboarding Auto Redirect', {
        source: 'course_dashboard',
        user_has_courses: userHasCourses,
      });
      navigate('/onboarding');
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (showArchived) {
      loadArchivedCourses();
    } else {
      loadCoursesAndCheckUser();
    }
  }, [showArchived]);

  const loadCoursesAndCheckUser = async () => {
    try {
      setLoading(true);
      
      // Load courses and user data in parallel
      const [coursesData, userData] = await Promise.all([
        coursesApi.getAllCourses(),
        authApi.getCurrentUser()
      ]);
      
      setCourses(coursesData);
      setCurrentUser(userData);
      maybeRedirectToOnboarding(coursesData);
      
      // Check for special easter egg (once every 3 days)
      const lastShownTimestamp = localStorage.getItem('special_easter_egg_last_shown');
      const threeDaysInMs = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds
      const now = Date.now();
      
      const shouldShowEasterEgg = !lastShownTimestamp || (now - parseInt(lastShownTimestamp)) > threeDaysInMs;
      
      if (shouldShowEasterEgg) {
        try {
          // Try to fetch easter egg text from backend
          const getApiBaseUrl = () => {
            if (process.env.NODE_ENV === 'production') {
              return window.location.origin;
            }
            return process.env.REACT_APP_API_URL || 'http://localhost:8000';
          };
          
          const easterEggResponse = await fetch(`${getApiBaseUrl()}/api/auth/easter-egg`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (easterEggResponse.ok) {
            const easterEggData = await easterEggResponse.json();
            setEasterEggText(easterEggData);
            
            // Delay the easter egg a bit so the page loads first
            setTimeout(() => {
              setShowSpecialEasterEgg(true);
              localStorage.setItem('special_easter_egg_last_shown', now.toString());
            }, 2000);
          }
        } catch (error) {
          // Silently fail if not available
        }
      }
      
      // Load sessions for each course
      const sessionsData: { [courseId: number]: StudySession[] } = {};
      for (const course of coursesData) {
        try {
          const courseSessions = await coursesApi.getCourseSessions(course.id);
          sessionsData[course.id] = courseSessions;
        } catch (err) {
          console.error(`Failed to load sessions for course ${course.id}:`, err);
          sessionsData[course.id] = [];
        }
      }
      setSessions(sessionsData);
    } catch (err) {
      setError('Failed to load courses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      setLoading(true);
      const coursesData = await coursesApi.getAllCourses();
      setCourses(coursesData);
      maybeRedirectToOnboarding(coursesData);
      
      // Load sessions for each course
      const sessionsData: { [courseId: number]: StudySession[] } = {};
      for (const course of coursesData) {
        try {
          const courseSessions = await coursesApi.getCourseSessions(course.id);
          sessionsData[course.id] = courseSessions;
        } catch (err) {
          console.error(`Failed to load sessions for course ${course.id}:`, err);
          sessionsData[course.id] = [];
        }
      }
      setSessions(sessionsData);
    } catch (err) {
      setError('Failed to load courses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCourseName.trim()) return;

    try {
      const newCourse = await coursesApi.createCourse({
        name: newCourseName.trim(),
        description: newCourseDescription.trim() || undefined
      });
      
      // Track course creation
      analytics.trackCourseCreated(newCourse.id, newCourse.name);
      
      setNewCourseName('');
      setNewCourseDescription('');
      setShowCreateCourse(false);
      await loadCourses();
    } catch (err) {
      analytics.trackError('Course Creation Failed', { 
        courseName: newCourseName.trim() 
      });
      setError('Failed to create course');
      console.error(err);
    }
  };

  const loadArchivedCourses = async () => {
    try {
      setLoading(true);
      const archivedData = await coursesApi.getArchivedCourses();
      setArchivedCourses(archivedData);
      
      // Load sessions for each archived course
      const sessionsData: { [courseId: number]: StudySession[] } = {};
      for (const course of archivedData) {
        try {
          const courseSessions = await coursesApi.getCourseSessions(course.id);
          sessionsData[course.id] = courseSessions;
        } catch (err) {
          console.error(`Failed to load sessions for archived course ${course.id}:`, err);
          sessionsData[course.id] = [];
        }
      }
      setSessions(sessionsData);
    } catch (err) {
      setError('Failed to load archived courses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveCourse = async (courseId: number, courseName: string) => {
    if (!window.confirm(t('dashboard:course.archive_confirm', { courseName }))) {
      return;
    }

    try {
      await coursesApi.archiveCourse(courseId);
      
      // Track course archiving
      analytics.track('Course Archived', {
        course_id: courseId,
        course_name: courseName
      });
      
      // Refresh the courses list
      if (showArchived) {
        await loadArchivedCourses();
      } else {
        await loadCourses();
      }
    } catch (err) {
      analytics.trackError('Course Archive Failed', { 
        courseId, 
        courseName 
      });
      setError('Failed to archive course');
      console.error(err);
    }
  };

  const handleUnarchiveCourse = async (courseId: number, courseName: string) => {
    if (!window.confirm(t('dashboard:course.unarchive_confirm', { courseName }))) {
      return;
    }

    try {
      await coursesApi.unarchiveCourse(courseId);
      
      // Track course unarchiving
      analytics.track('Course Unarchived', {
        course_id: courseId,
        course_name: courseName
      });
      
      // Refresh the archived courses list
      await loadArchivedCourses();
    } catch (err) {
      analytics.trackError('Course Unarchive Failed', { 
        courseId, 
        courseName 
      });
      setError('Failed to restore course');
      console.error(err);
    }
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setEditCourseName(course.name);
    setEditCourseDescription(course.description || '');
  };

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCourse || !editCourseName.trim()) return;

    try {
      const updatedCourse = await coursesApi.updateCourse(editingCourse.id, {
        name: editCourseName.trim(),
        description: editCourseDescription.trim() || undefined
      });
      
      // Track course update
      analytics.track('Course Updated', {
        course_id: editingCourse.id,
        old_name: editingCourse.name,
        new_name: editCourseName.trim(),
        name_changed: editingCourse.name !== editCourseName.trim(),
        description_changed: (editingCourse.description || '') !== (editCourseDescription.trim() || '')
      });
      
      // Update the course in state
      setCourses(courses.map(c => c.id === editingCourse.id ? updatedCourse : c));
      
      // Reset edit state
      setEditingCourse(null);
      setEditCourseName('');
      setEditCourseDescription('');
    } catch (err) {
      analytics.trackError('Course Update Failed', { 
        courseId: editingCourse.id,
        courseName: editCourseName.trim() 
      });
      setError('Failed to update course');
      console.error(err);
    }
  };

  const handleDeleteCourse = async (courseId: number, courseName: string) => {
    if (!window.confirm(t('dashboard:course.delete_confirm', { courseName }))) {
      return;
    }

    try {
      await coursesApi.deleteCourse(courseId);
      
      // Track course deletion
      analytics.track('Course Deleted', {
        course_id: courseId,
        course_name: courseName,
        sessions_count: sessions[courseId]?.length || 0
      });
      
      setCourses(courses.filter(c => c.id !== courseId));
      const newSessions = { ...sessions };
      delete newSessions[courseId];
      setSessions(newSessions);
    } catch (err) {
      analytics.trackError('Course Deletion Failed', { 
        courseId, 
        courseName 
      });
      setError('Failed to delete course');
      console.error(err);
    }
  };

  const handleOpenOnboarding = () => {
    analytics.track('Onboarding Reopened', {
      source: 'dashboard_help_icon'
    });
    navigate('/onboarding');
  };

  const handleResetOnboarding = () => {
    if (window.confirm('Reset onboarding? This will show the onboarding flow again on next refresh.')) {
      resetOnboarding();
      alert('Onboarding reset! Refresh the page or navigate to /onboarding to see it again.');
    }
  };

  const dismissSpecialEasterEgg = () => {
    setShowSpecialEasterEgg(false);
  };

  const formatDate = (dateString: string) => {
    const locale = isRTL ? 'he-IL' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vibrant-orange"></div>
      </div>
    );
  }

  return (
    <DashboardContainer>
      {error && (
        <Card className="border-vibrant-orange/30 bg-vibrant-orange/10 backdrop-blur-sm mb-8">
          <p className="text-vibrant-orange font-medium">⚠️ {error}</p>
        </Card>
      )}

      {/* Quick Actions Section */}
      <div className="mb-6 md:mb-16">
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold mb-4 font-hebrew text-bg-primary">
            {t('dashboard:quick_actions.title')}
          </h1>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 px-4">
          {isRTL ? (
            <>
              {/* Summarize Document - Top Right in RTL */}
              <button
                onClick={() => navigate('/quick/create-study-session')}
                className={`p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                  bg-gradient-to-br from-vibrant-orange/80 to-vibrant-orange/90 
                  hover:from-vibrant-orange/90 hover:to-vibrant-orange/95
                  backdrop-blur-sm border border-vibrant-orange/30 
                  shadow-lg hover:shadow-xl hover:shadow-vibrant-orange/20
                  text-astronaut-white hover:text-white`}
              >
                <img 
                  src="/astronaut_reading.png" 
                  alt="StudyBel Astronaut Reading" 
                  className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
                />
                <span className="text-lg md:text-xl">
                  <MixedText>{t('dashboard:quick_actions.summarize_document')}</MixedText>
                </span>
              </button>

              {/* Summarize Lecture - Top Left in RTL */}
              <button
                onClick={() => navigate('/quick/create-study-session')}
                className={`p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                  bg-gradient-to-br from-vibrant-orange/80 to-vibrant-orange/90 
                  hover:from-vibrant-orange/90 hover:to-vibrant-orange/95
                  backdrop-blur-sm border border-vibrant-orange/30 
                  shadow-lg hover:shadow-xl hover:shadow-vibrant-orange/20
                  text-astronaut-white hover:text-white`}
              >
                <img 
                  src="/astronaut_video.png" 
                  alt="StudyBel Astronaut Video" 
                  className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
                />
                <span className="text-lg md:text-xl">
                  <MixedText>{t('dashboard:quick_actions.summarize_lecture')}</MixedText>
                </span>
              </button>

              {/* Generate Exam - Bottom Right in RTL */}
              <button
                onClick={() => navigate('/exams/ai-generate?source=dashboard')}
                className={`p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''}
                  bg-gradient-to-br from-muted-purple/80 to-deep-space-purple/90
                  hover:from-muted-purple/90 hover:to-deep-space-purple/95
                  backdrop-blur-sm border border-muted-purple/30
                  shadow-lg hover:shadow-xl hover:shadow-muted-purple/20
                  text-astronaut-white hover:text-white`}
              >
                <img 
                  src="/astronaut_idea.png" 
                  alt="StudyBel Astronaut Idea" 
                  className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
                />
                <span className="text-lg md:text-xl">
                  <MixedText>{t('dashboard:quick_actions.generate_exam')}</MixedText>
                </span>
              </button>

              {/* Solve Exam - Bottom Left in RTL */}
              <button
                onClick={() => navigate('/exams/create?source=dashboard')}
                className={`p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                  bg-gradient-to-br from-muted-purple/80 to-deep-space-purple/90 
                  hover:from-muted-purple/90 hover:to-deep-space-purple/95
                  backdrop-blur-sm border border-muted-purple/30 
                  shadow-lg hover:shadow-xl hover:shadow-muted-purple/20
                  text-astronaut-white hover:text-white`}
              >
                <img 
                  src="/astronaut_checkbox.png" 
                  alt="StudyBel Astronaut Checkbox" 
                  className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
                />
                <span className="text-lg md:text-xl">
                  <MixedText>{t('dashboard:quick_actions.solve_exam')}</MixedText>
                </span>
              </button>
            </>
          ) : (
            <>
              {/* Summarize Lecture - Top Left in LTR */}
              <button
                onClick={() => navigate('/quick/create-study-session')}
                className={`p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                  bg-gradient-to-br from-vibrant-orange/80 to-vibrant-orange/90 
                  hover:from-vibrant-orange/90 hover:to-vibrant-orange/95
                  backdrop-blur-sm border border-vibrant-orange/30 
                  shadow-lg hover:shadow-xl hover:shadow-vibrant-orange/20
                  text-astronaut-white hover:text-white`}
              >
                <img 
                  src="/astronaut_video.png" 
                  alt="StudyBel Astronaut Video" 
                  className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
                />
                <span className="text-lg md:text-xl">
                  <MixedText>{t('dashboard:quick_actions.summarize_lecture')}</MixedText>
                </span>
              </button>

              {/* Summarize Document - Top Right in LTR */}
              <button
                onClick={() => navigate('/quick/create-study-session')}
                className={`p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                  bg-gradient-to-br from-vibrant-orange/80 to-vibrant-orange/90 
                  hover:from-vibrant-orange/90 hover:to-vibrant-orange/95
                  backdrop-blur-sm border border-vibrant-orange/30 
                  shadow-lg hover:shadow-xl hover:shadow-vibrant-orange/20
                  text-astronaut-white hover:text-white`}
              >
                <img 
                  src="/astronaut_reading.png" 
                  alt="StudyBel Astronaut Reading" 
                  className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
                />
                <span className="text-lg md:text-xl">
                  <MixedText>{t('dashboard:quick_actions.summarize_document')}</MixedText>
                </span>
              </button>

              {/* Solve Exam - Bottom Left in LTR */}
              <button
                onClick={() => navigate('/exams/create?source=dashboard')}
                className={`p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                  bg-gradient-to-br from-muted-purple/80 to-deep-space-purple/90 
                  hover:from-muted-purple/90 hover:to-deep-space-purple/95
                  backdrop-blur-sm border border-muted-purple/30 
                  shadow-lg hover:shadow-xl hover:shadow-muted-purple/20
                  text-astronaut-white hover:text-white`}
              >
                <img 
                  src="/astronaut_checkbox.png" 
                  alt="StudyBel Astronaut Checkbox" 
                  className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
                />
                <span className="text-lg md:text-xl">
                  <MixedText>{t('dashboard:quick_actions.solve_exam')}</MixedText>
                </span>
              </button>

              {/* Generate Exam - Bottom Right in LTR */}
              <button
                onClick={() => navigate('/exams/ai-generate?source=dashboard')}
                className={`p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
                  bg-gradient-to-br from-muted-purple/80 to-deep-space-purple/90 
                  hover:from-muted-purple/90 hover:to-deep-space-purple/95
                  backdrop-blur-sm border border-muted-purple/30 
                  shadow-lg hover:shadow-xl hover:shadow-muted-purple/20
                  text-astronaut-white hover:text-white`}
              >
                <img 
                  src="/astronaut_idea.png" 
                  alt="StudyBel Astronaut Idea" 
                  className={`w-16 h-16 md:w-20 md:h-20 ${isRTL ? 'ml-3' : 'mr-3'} filter brightness-110`}
                />
                <span className="text-lg md:text-xl">
                  <MixedText>{t('dashboard:quick_actions.generate_exam')}</MixedText>
                </span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Courses Header */}
      <div className="text-center mb-6 md:mb-16 relative">
        {/* Background decorative elements */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/4 w-32 h-32 bg-gradient-to-br from-vibrant-orange/10 to-muted-purple/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-8 right-1/4 w-24 h-24 bg-gradient-to-br from-muted-purple/10 to-vibrant-orange/10 rounded-full blur-2xl animate-pulse delay-700"></div>
        </div>
        
        {/* Main title */}
        <div className="text-center">
          <h1 className="text-2xl md:text-4xl font-bold mb-4 font-hebrew text-bg-primary">
            {showArchived ? t('dashboard:course.archived_title') : t('dashboard:header.title')}
          </h1>
        </div>
      </div>

      {/* Create Course Modal */}
      {showCreateCourse && (
        <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ background: 'rgba(26, 15, 43, 0.5)' }}>
          <Card className="w-full max-w-lg shadow-2xl mx-4">
            {/* Modal Header */}
            <CardHeader>
              <CardTitle className={`font-hebrew ${isRTL ? 'text-right' : ''}`}>
                🚀 {t('dashboard:course_card.create_new_course')}
              </CardTitle>
            </CardHeader>

            <form onSubmit={handleCreateCourse} className="space-y-5">
              {/* Course Name Field */}
              <div>
                <label htmlFor="courseName" className={`block text-sm font-semibold text-surface-secondary mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    📚 {t('common:labels.course_name')} *
                  </span>
                </label>
                <input
                  type="text"
                  id="courseName"
                  value={newCourseName}
                  onChange={(e) => setNewCourseName(e.target.value)}
                  placeholder={t('common:placeholders.course_name_example')}
                  className={`w-full px-4 py-3 border-2 border-surface-muted/30 rounded-lg focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all duration-200 font-hebrew ${isRTL ? 'text-right' : ''}`}
                  required
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              {/* Course Description Field */}
              <div>
                <label htmlFor="courseDescription" className={`block text-sm font-semibold text-surface-secondary mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    📝 {t('common:labels.description')} ({isRTL ? 'אופציונלי' : 'Optional'})
                  </span>
                </label>
                <textarea
                  id="courseDescription"
                  value={newCourseDescription}
                  onChange={(e) => setNewCourseDescription(e.target.value)}
                  placeholder={t('common:placeholders.course_description')}
                  className={`w-full px-4 py-3 border-2 border-surface-muted/30 rounded-lg focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all duration-200 resize-none font-hebrew ${isRTL ? 'text-right' : ''}`}
                  rows={3}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              {/* Action Buttons */}
              <CardFooter className="flex flex-col space-y-3 pt-4">
                                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={!newCourseName.trim()}
                  className="w-full font-hebrew"
                >
                  🚀 {t('common:buttons.create')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={() => {
                    setShowCreateCourse(false);
                    setNewCourseName('');
                    setNewCourseDescription('');
                  }}
                  className="w-full font-hebrew"
                >
                  {t('common:buttons.cancel')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      )}

      {/* Edit Course Modal */}
      {editingCourse && (
        <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ background: 'rgba(26, 15, 43, 0.5)' }}>
          <Card className="w-full max-w-lg shadow-2xl mx-4">
            {/* Modal Header */}
            <CardHeader>
              <CardTitle className={`font-hebrew ${isRTL ? 'text-right' : ''}`}>
                ✨ {t('common:actions.edit_course')}
              </CardTitle>
            </CardHeader>

            <form onSubmit={handleUpdateCourse} className="space-y-5">
              {/* Course Name Field */}
              <div>
                <label htmlFor="editCourseName" className={`block text-sm font-semibold text-surface-secondary mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    📚 {t('common:labels.course_name')} *
                  </span>
                </label>
                <input
                  type="text"
                  id="editCourseName"
                  value={editCourseName}
                  onChange={(e) => setEditCourseName(e.target.value)}
                  placeholder={t('common:placeholders.course_name_example')}
                  className={`w-full px-4 py-3 border-2 border-surface-muted/30 rounded-lg focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all duration-200 font-hebrew ${isRTL ? 'text-right' : ''}`}
                  required
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              {/* Course Description Field */}
              <div>
                <label htmlFor="editCourseDescription" className={`block text-sm font-semibold text-surface-secondary mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    📝 {t('common:labels.description')} ({isRTL ? 'אופציונלי' : 'Optional'})
                  </span>
                </label>
                <textarea
                  id="editCourseDescription"
                  value={editCourseDescription}
                  onChange={(e) => setEditCourseDescription(e.target.value)}
                  placeholder={t('common:placeholders.course_description')}
                  className={`w-full px-4 py-3 border-2 border-surface-muted/30 rounded-lg focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all duration-200 resize-none font-hebrew ${isRTL ? 'text-right' : ''}`}
                  rows={3}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              {/* Action Buttons */}
              <CardFooter className="flex flex-col space-y-3 pt-4">
                                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={!editCourseName.trim()}
                  className="w-full font-hebrew"
                >
                  ✨ {t('common:buttons.update')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={() => {
                    setEditingCourse(null);
                    setEditCourseName('');
                    setEditCourseDescription('');
                  }}
                  className="w-full font-hebrew"
                >
                  {t('common:buttons.cancel')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      )}

      {/* Courses Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 max-w-6xl mx-auto px-2 md:px-4">
        {/* Add Course Card - Show first (left) for RTL */}
        {isRTL && !showArchived && (
          <SpaceCard 
            onClick={() => setShowCreateCourse(true)}
            className="cursor-pointer border-2 border-dashed border-vibrant-orange/30 hover:border-vibrant-orange/50 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] min-h-[240px] md:min-h-[280px] flex flex-col items-center justify-center text-center"
            constellation="orion"
          >
            <div className="p-6 md:p-8 flex flex-col items-center justify-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-vibrant-orange/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-vibrant-orange/20 transition-colors duration-300">
                <Plus className="w-6 h-6 md:w-8 md:h-8 text-vibrant-orange group-hover:scale-110 transition-transform duration-300" />
              </div>
              <CardTitle className="text-lg md:text-xl mb-2 font-hebrew text-surface-primary">
                🚀 {t('dashboard:course_card.create_new_course')}
              </CardTitle>
              <p className="text-surface-secondary text-sm max-w-xs font-hebrew">
                {t('dashboard:course_card.start_learning_journey')}
              </p>
            </div>
          </SpaceCard>
        )}

        {/* Existing Courses */}
        {(showArchived ? archivedCourses : courses).map((course) => {
          const courseSessions = sessions[course.id] || [];
          const totalSessions = courseSessions.length;
          const completedSessions = courseSessions.filter(s => s.completed_at).length;
          const progressPercentage = totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0;
          
          // Determine course state and next action
          const getCourseState = () => {
            if (totalSessions === 0) return 'empty';
            return 'in_progress';
          };
          
          const courseState = getCourseState();
          
          return (
            <SpaceCard key={course.id} className="group hover:shadow-xl transition-all duration-300 hover:scale-[1.02] flex flex-col" constellation="cassiopeia">
              <Link to={`/course/${course.id}`} className="block flex-1">
                {/* Course Header */}
                <CardHeader>
                  <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                    <div className={`hidden md:flex items-center justify-center w-14 h-14 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/20 rounded-xl flex-shrink-0 group-hover:shadow-lg transition-shadow duration-300 ${isRTL ? 'order-2' : 'order-1'}`}>
                      <BookOpen className="w-7 h-7 text-vibrant-orange" />
                    </div>
                    <div className={`flex-1 min-w-0 ${isRTL ? 'md:order-1 text-right md:pr-2' : 'md:order-2'}`}>
                      <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'justify-end' : ''}`}>
                        <CardTitle className={`text-lg md:text-xl line-clamp-2 group-hover:text-vibrant-orange transition-colors duration-300 ${isRTL ? 'text-right' : ''}`}>
                          {course.name}
                        </CardTitle>
                        {showArchived && (
                          <span className="px-2 py-1 text-xs bg-surface-muted/30 text-surface-secondary rounded-full font-hebrew">
                            {t('dashboard:course.archived_badge')}
                          </span>
                        )}
                      </div>
                      {course.description && (
                        <CardContent className={`text-sm mb-3 line-clamp-2 leading-relaxed ${isRTL ? 'text-right' : ''}`}>
                          {course.description}
                        </CardContent>
                      )}
                      <div className={`flex items-center text-xs text-surface-muted ${isRTL ? 'justify-end' : ''}`}>
                        <span className={`flex items-center font-medium font-hebrew ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Users className={`w-3 h-3 ${isRTL ? 'mr-2 ml-1' : 'mr-1'}`} />
                          {totalSessions} {totalSessions !== 1 ? t('common:labels.sessions') : t('common:labels.session')}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                {/* Course Preview & Actions */}
                <CardContent>
                  {courseState === 'empty' && (
                    <div className="space-y-4">
                      <div className="text-center py-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-muted-purple/10 to-muted-purple/20 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Plus className="w-6 h-6 text-muted-purple" />
                        </div>
                        <p className="text-surface-primary font-semibold mb-1 font-hebrew">🚀 {t('dashboard:course_card.ready_to_start')}</p>
                        <p className="text-xs text-surface-muted font-hebrew">{t('dashboard:course_card.create_first_session')}</p>
                      </div>
                      <div className="space-y-2">
                        <Link
                          to={`/create-session?course_id=${course.id}`}
                          className="group relative overflow-hidden w-full bg-gradient-to-r from-vibrant-orange to-vibrant-orange/80 text-astronaut-white py-4 px-6 md:py-4 md:px-6 rounded-xl font-semibold text-base hover:shadow-xl hover:shadow-vibrant-orange/30 transition-all duration-300 flex items-center justify-center font-hebrew hover:scale-105"
                          style={{
                            boxShadow: '0 8px 24px rgba(247, 103, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                          }}
                        >
                          {/* Glow effect overlay */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-300 transform -skew-x-12" />
                          
                          {/* Content */}
                          <span className="relative flex items-center justify-center space-x-2">
                            <span className="text-lg md:text-lg">🛸</span>
                            <span>{t('dashboard:course_card.start_first_session')}</span>
                          </span>
                        </Link>
                      </div>
                    </div>
                  )}
                  
                  {(courseState === 'in_progress') && (
                    <div className="space-y-4">
                      {/* Latest Session Preview */}
                      {(() => {
                        const latestSession = courseSessions.length > 0 ? courseSessions[courseSessions.length - 1] : null;
                        const inProgressSession = courseSessions.find(s => !s.completed_at && ((s.transcriptions?.length || 0) > 0 || (s.documents?.length || 0) > 0));
                        const targetSession = inProgressSession || latestSession;
                        
                        return targetSession ? (
                          <div className="bg-gradient-to-r from-deep-space-purple/5 to-muted-purple/5 rounded-lg p-3 border border-muted-purple/20">
                            <div className={`flex items-start justify-between mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
                                <p className={`text-xs text-surface-muted font-hebrew mb-1 ${isRTL ? 'text-right' : ''}`}>
                                  <MixedText>{`${t('dashboard:course_card.current_session')}`}</MixedText>
                                </p>
                                <p className={`text-sm font-medium text-surface-primary line-clamp-1 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                                  {targetSession.title}
                                </p>
                              </div>
                              <div className={`${isRTL ? 'mr-2 text-left' : 'ml-2 text-right'}`}>
                                <div className="text-xs text-surface-muted">
                                  {(() => {
                                    let progress = 0;
                                    // Step 1: Upload Content (25%)
                                    if ((targetSession.transcriptions?.length || 0) > 0 || (targetSession.documents?.length || 0) > 0) {
                                      progress += 25;
                                    }
                                    // Step 2: Generate Materials (25%)
                                    if ((targetSession.study_materials?.length || 0) > 0) {
                                      progress += 25;
                                    }
                                    // Step 3: Practice Questions (25%)
                                    if ((targetSession.questions?.length || 0) > 0) {
                                      progress += 25;
                                    }
                                    // Step 4: Mark Complete (25%)
                                    if (targetSession.completed_at) {
                                      progress += 25;
                                    }
                                    return Math.min(progress, 100);
                                  })()}%
                                </div>
                                <div className="w-8 h-1 bg-surface-muted/20 rounded-full mt-1">
                                  <div 
                                    className={`h-full bg-gradient-to-r ${isRTL ? 'from-vibrant-orange to-vibrant-orange/70' : 'from-vibrant-orange/70 to-vibrant-orange'} rounded-full transition-all duration-300`}
                                    style={{ 
                                      width: `${(() => {
                                        let progress = 0;
                                        if ((targetSession.transcriptions?.length || 0) > 0 || (targetSession.documents?.length || 0) > 0) {
                                          progress += 25;
                                        }
                                        if ((targetSession.study_materials?.length || 0) > 0) {
                                          progress += 25;
                                        }
                                        if ((targetSession.questions?.length || 0) > 0) {
                                          progress += 25;
                                        }
                                        if (targetSession.completed_at) {
                                          progress += 25;
                                        }
                                        return Math.min(progress, 100);
                                      })()}%`,
                                      float: isRTL ? 'right' : 'left'
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                            {targetSession.ai_generated_description && (
                              <p className={`text-xs text-surface-muted line-clamp-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                                {targetSession.ai_generated_description}
                              </p>
                            )}
                          </div>
                        ) : null;
                      })()}
                      
                      {/* Progress Overview - Hidden on mobile, shown on larger screens */}
                      <div className="hidden md:flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-surface-primary font-hebrew">
                            {'🌟'} {progressPercentage}% {t('dashboard:course_card.complete')}
                          </p>
                          <p className="text-xs text-surface-muted font-hebrew">
                            {completedSessions} / {totalSessions} {t('common:labels.sessions')}
                          </p>
                        </div>
                        <div className="w-12 h-12 md:w-16 md:h-16 relative flex-shrink-0">
                          <svg className="w-12 h-12 md:w-16 md:h-16 transform -rotate-90" viewBox="0 0 64 64" style={{ overflow: 'visible' }}>
                            <circle
                              cx="32"
                              cy="32"
                              r="26"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="4"
                              className="text-surface-muted/20"
                            />
                            <circle
                              cx="32"
                              cy="32"
                              r="26"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="4"
                              strokeDasharray={`${(Math.min(progressPercentage, 100) / 100) * 163.36} 163.36`}
                              className="text-vibrant-orange transition-all duration-500"
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-base md:text-lg">{progressPercentage < 100 ? '🪐' : '⭐'}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Button */}
                      <div className="flex justify-center">
                        <Link
                          to={`/course/${course.id}`}
                          className="group relative overflow-hidden bg-gradient-to-r from-vibrant-orange to-vibrant-orange/80 text-astronaut-white py-4 px-8 md:py-4 md:px-8 rounded-xl font-semibold text-base hover:shadow-xl hover:shadow-vibrant-orange/30 transition-all duration-300 text-center font-hebrew hover:scale-105 min-w-[160px] md:min-w-[160px]"
                          style={{
                            boxShadow: '0 8px 24px rgba(247, 103, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                          }}
                        >
                          {/* Glow effect overlay */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-300 transform -skew-x-12" />
                          
                          {/* Content */}
                          <span className="relative flex items-center justify-center space-x-2">
                            <span>🚀</span>
                            <span>{t('dashboard:course_card.continue')}</span>
                          </span>
                        </Link>
                      </div>
                    </div>
                  )}
                  

                </CardContent>
              </Link>
              
              {/* Action Bar */}
              <CardFooter align="center" className="border-t border-surface-muted/20">
                <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                  {!showArchived && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditCourse(course);
                      }}
                      className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-orange/10 hover:text-vibrant-orange flex items-center justify-center"
                      aria-label={t('common:actions.edit_course')}
                    >
                      <Edit3 className="w-4 h-4 md:w-4 md:h-4" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (showArchived) {
                        handleUnarchiveCourse(course.id, course.name);
                      } else {
                        handleArchiveCourse(course.id, course.name);
                      }
                    }}
                    className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-orange/10 hover:text-vibrant-orange flex items-center justify-center"
                    aria-label={showArchived ? t('common:actions.unarchive_course') : t('common:actions.archive_course')}
                  >
                    {showArchived ? <ArchiveRestore className="w-4 h-4 md:w-4 md:h-4" /> : <Archive className="w-4 h-4 md:w-4 md:h-4" />}
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteCourse(course.id, course.name);
                    }}
                    className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-orange/10 hover:text-vibrant-orange flex items-center justify-center"
                    aria-label={t('common:actions.delete_course')}
                  >
                    <Trash2 className="w-4 h-4 md:w-4 md:h-4" />
                  </button>
                </div>
              </CardFooter>
            </SpaceCard>
          );
        })}

        {/* Add Course Card - Show last (right) for LTR */}
        {!isRTL && !showArchived && (
          <SpaceCard 
            onClick={() => setShowCreateCourse(true)}
            className="cursor-pointer border-2 border-dashed border-vibrant-orange/30 hover:border-vibrant-orange/50 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] min-h-[240px] md:min-h-[280px] flex flex-col items-center justify-center text-center"
            constellation="ursa"
          >
            <div className="p-6 md:p-8 flex flex-col items-center justify-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-vibrant-orange/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-vibrant-orange/20 transition-colors duration-300">
                <Plus className="w-6 h-6 md:w-8 md:h-8 text-vibrant-orange group-hover:scale-110 transition-transform duration-300" />
              </div>
              <CardTitle className="text-lg md:text-xl mb-2 font-hebrew text-surface-primary">
                🚀 {t('dashboard:course_card.create_new_course')}
              </CardTitle>
              <p className="text-surface-secondary text-sm max-w-xs font-hebrew">
                {t('dashboard:course_card.start_learning_journey')}
              </p>
            </div>
          </SpaceCard>
        )}
      </div>

      {/* View Archived Button - Show when there's more than 1 course total and not in archived view */}
      {!showArchived && courses.length > 0 && (
        <div className="mt-8 text-center">
          <Button
            variant="secondary"
            size="md"
            onClick={() => setShowArchived(true)}
            className="font-hebrew mx-auto"
            icon={<Archive />}
            iconPosition="left"
          >
            {t('dashboard:course.view_archived')}
          </Button>
        </div>
      )}

      {/* Empty State for Archived Courses */}
      {showArchived && archivedCourses.length === 0 && (
        <div className="text-center py-12">
          <Archive className="w-16 h-16 text-bg-primary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-bg-primary mb-2 font-hebrew">
            {t('dashboard:course.archived_empty')}
          </h3>
          <p className="text-bg-primary font-hebrew">
            {t('dashboard:course.archived_empty_description')}
          </p>
        </div>
      )}

      {/* Back to Active Courses Button - Show when in archived view */}
      {showArchived && (
        <div className="mt-8 text-center">
          <Button
            variant="secondary"
            size="md"
            onClick={() => setShowArchived(false)}
            className="font-hebrew mx-auto"
            icon={<ArrowLeft />}
            iconPosition="left"
          >
            {t('dashboard:course.back_to_active')}
          </Button>
        </div>
      )}

      <div className="fixed left-4 bottom-4 z-[60] flex flex-col gap-3 items-start">
        <button
          onClick={handleOpenOnboarding}
          className="w-12 h-12 rounded-full bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 text-white shadow-lg shadow-vibrant-orange/30 border border-vibrant-orange/40 hover:scale-105 transition-transform duration-200 flex items-center justify-center"
          aria-label={t('onboarding:revisit.button', 'Need help? Replay onboarding')}
          title={t('onboarding:revisit.button', 'Need help? Replay onboarding')}
        >
          <HelpCircle className="w-6 h-6" />
        </button>

        {isDev && (
          <div className="bg-surface-base/90 backdrop-blur-sm rounded-lg p-2 border border-surface-muted/30">
            <div className="text-xs text-surface-muted mb-2 px-2">Dev Tools</div>
            <div className="space-y-1">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleResetOnboarding}
                className="flex items-center gap-2 w-full text-xs"
              >
                <RotateCcw className="w-3 h-3" />
                Reset Onboarding
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowUpgradeModal(true)}
                className="flex items-center gap-2 w-full text-xs"
              >
                <Zap className="w-3 h-3" />
                Upgrade Modal
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Special Easter Egg - Flying Astronaut with Love Message */}
      {showSpecialEasterEgg && easterEggText && (
        <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-in fade-in duration-1000"
            style={{ pointerEvents: 'auto' }}
            onClick={dismissSpecialEasterEgg}
          />
          
          {/* Flying Astronaut with Message */}
          <div className="relative animate-in slide-in-from-right-full duration-2000 ease-out" style={{ pointerEvents: 'auto' }}>
            <div className="bg-gradient-to-br from-vibrant-orange/90 to-vibrant-orange/80 backdrop-blur-sm border border-vibrant-orange/50 shadow-2xl rounded-3xl p-10 max-w-2xl mx-4 transform hover:scale-105 transition-all duration-300">
              {/* Close button */}
              <button 
                onClick={dismissSpecialEasterEgg} 
                className="absolute top-4 right-4 p-3 rounded-full hover:bg-white/20 transition-colors duration-200 text-white"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
              
              {/* Full width astronaut at top */}
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <img 
                    src="/astronaut_loving.png" 
                    alt="StudyBel Astronaut" 
                    className="w-48 h-48 animate-bounce filter brightness-110" 
                    style={{ animationDuration: '2s' }}
                  />
                  {/* Heart particles around astronaut */}
                  <div className="absolute -top-4 -right-4 text-red-400 animate-pulse text-3xl">💕</div>
                  <div className="absolute top-8 -left-8 text-pink-300 animate-ping text-2xl" style={{ animationDelay: '0.5s' }}>💖</div>
                  <div className="absolute bottom-4 right-8 text-red-300 animate-pulse text-xl" style={{ animationDelay: '1s' }}>💗</div>
                  <div className="absolute -bottom-2 -left-4 text-pink-400 animate-pulse text-lg" style={{ animationDelay: '1.5s' }}>💘</div>
                </div>
              </div>
              
              {/* Centered text content */}
              <div className="text-center space-y-6">
                {/* Main message */}
                <div className="text-white font-bold text-4xl font-hebrew">
                  {easterEggText.message}
                </div>
                
                {/* Quote in a box */}
                <div className="bg-white/10 rounded-xl p-6 border border-white/20 mt-8">
                  <p className="text-white font-medium text-2xl font-hebrew leading-relaxed">
                    {easterEggText.quote}
                  </p>
                </div>
              </div>
              
              {/* Floating hearts animation */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
                <div className="absolute bottom-full left-1/4 text-red-300 animate-float-up opacity-70 text-xl">💕</div>
                <div className="absolute bottom-full right-1/3 text-pink-300 animate-float-up opacity-60 text-lg" style={{ animationDelay: '1s' }}>💖</div>
                <div className="absolute bottom-full left-1/2 text-red-400 animate-float-up opacity-80 text-xl" style={{ animationDelay: '2s' }}>💗</div>
                <div className="absolute bottom-full left-3/4 text-pink-400 animate-float-up opacity-50 text-sm" style={{ animationDelay: '3s' }}>💘</div>
                <div className="absolute bottom-full right-1/4 text-red-200 animate-float-up opacity-70 text-lg" style={{ animationDelay: '4s' }}>💕</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dev Modal Components */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />

      {/* Feedback Chat Widget */}
      <FeedbackChatWidget
        isOpen={showFeedbackChat}
        onClose={() => setShowFeedbackChat(false)}
        onOpen={() => setShowFeedbackChat(true)}
      />

    </DashboardContainer>
  );
};

export default CourseDashboard; 
